import { IconFileWithItemsOutline, IconPlus, IconUpload } from '@iwoca/icons';
import { Button, Card, FileDrop, Label } from '@iwoca/orion';
import clsx from 'clsx';
import { useFormContext } from 'react-hook-form';
import { FieldError } from '../FieldError/FieldError';
import sharedStyles from '../RegistrationForm.module.css';
import {
  TFileWithDocType,
  TDocType,
  TRegistrationFormValues,
} from '../registrationForm.types';
import styles from './FileUpload.module.css';

type TProps = {
  group: TDocType;
  selectedFile?: TFileWithDocType;
  fileDropClassName?: string;
  cardClassName?: string;
  onChange: (file?: TFileWithDocType) => void;
};

export const FileUpload = ({
  group,
  selectedFile,
  fileDropClassName,
  cardClassName,
  onChange,
}: TProps) => {
  const {
    formState: { errors },
  } = useFormContext<TRegistrationFormValues>();

  const renderTextContent = () => {
    switch (group) {
      case 'proof_of_address': {
        return {
          label: 'Proof of business address',
          instruction:
            'Upload a recent bank statement, utility bill, or a letter from HMRC or Companies House, dated within the last 3 months and in PDF format.',
        };
      }
      case 'bank_statement': {
        return {
          label: 'Bank statements',
          instruction: (
            <>
              Upload a recent bank statement dated within the last 3 months and
              in PDF format.
              <ul className={styles.list}>
                <li>
                  This should be for the account(s) where the majority of the
                  business revenue is generated.
                </li>
                <li>
                  Please also ensure it displays the business name, sort code,
                  account number, and balance, and relate to the account where
                  most business revenue is received.
                </li>
              </ul>
            </>
          ),
        };
      }
      case 'certified_id': {
        return {
          label: 'Certified ID',
          instruction:
            'Upload a clear copy of your valid passport or driving licence in PDF, JPG, JPEG, or PNG format.',
        };
      }
    }
  };

  const handleOnChange = (files: File[]) => {
    // Should only be one file uploaded per group, so it will replace the previously selected file
    files.forEach((file) => {
      const newFileEntry = { file, docType: group };
      onChange(newFileEntry);
    });
  };

  return (
    <div className={sharedStyles.fileDropContainer}>
      <Label>{renderTextContent().label}</Label>
      <div className={sharedStyles.instruction}>
        {renderTextContent().instruction}
      </div>
      {!selectedFile && (
        <>
          <FileDrop
            disabled={Boolean(selectedFile)}
            multiple={false}
            className={clsx(sharedStyles.fileDrop, fileDropClassName)}
            accept=".jpg, .jpeg, .png, .pdf"
            data-testid={`${group}-file-upload`}
            onChange={handleOnChange}
            stacked={false}
          />
          <FieldError
            errorMessage={
              errors?.[
                group === 'bank_statement'
                  ? 'bankStatementFile'
                  : group === 'certified_id'
                    ? 'certifiedIdFile'
                    : 'proofOfAddressFile'
              ]?.message
            }
          />
        </>
      )}
      {selectedFile && (
        <Card
          key={`${selectedFile.docType}-${selectedFile.file.name}`}
          className={clsx(styles.card, cardClassName)}
        >
          <div className={styles.fileNameContainer}>
            <IconFileWithItemsOutline
              width={20}
              height={20}
              className={styles.icon}
            />
            <p className={styles.name}>{selectedFile.file.name}</p>
            <Button
              data-testid="remove-button"
              className={styles.removeButton}
              size="small"
              variant="tertiary"
              onClick={() => onChange(undefined)}
            >
              <IconPlus height={20} width={20} className={styles.removeIcon} />
            </Button>
          </div>
          <div className={styles.uploadedText}>
            <IconUpload width={16} height={16} className={styles.icon} />
            <span>Document uploaded</span>
          </div>
        </Card>
      )}
    </div>
  );
};
